import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardHeader,
  Grid,
  TextField,
  Tabs,
  Tab,
  useTheme,
  Alert,
  CircularProgress,
  Stack,
  useMediaQuery,
  MenuItem,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";

import { useNavigate, useParams } from "react-router-dom";
import { createProduct, updateProduct, fetchProductById, fetchProductCategories, createCategory, updateVariantStock  } from "../api/productApi";
import VariantManager from "../components/VariantManager";
import ImageUploader from "../components/ImageUploader";
import OptionsManager from "../components/OptionsManager";
import RichTextEditor from "../components/RichTextEditor";
import { useThemeContext } from "../context/ThemeContext";
import { useSnackbar } from "notistack";
import { fetchBrands, createBrand } from "../api/productApi";
import TieredPricingManager from "../components/TieredPricingManager";


type ProductUpdateData = {
  id?: string;
  name: string;
  description: string;
  category_id: number;
  brand_id: number;
  base_price: string;
  image: string;
  status: 'active' | 'inactive';
  min_order_qty: string; // 👈 added MOQ
  order_multiple: string;
  variants: Array<{
    id?: number;
    variant_name: string;
    price: string;
    stock_quantity: string;
    image: string;
    sku: string;
    selected_options: Record<string, string>;
    min_order_qty?: string; // 👈 also allow per-variant MOQ
    order_multiple?: string;
  }>;
  master_sku: string;
  stock_quantity: string;
  options?: Option[];
};


type Variant = {
  id?: number;
  variant_name: string;
  price: string;
  stock_quantity: string;
  image?: string;
  sku: string;
  selected_options?: Record<string, string>;
};

type Option = {
  id?: number;
  name: string;
  values: string[];
  isRequired: boolean;
};

type ProductForm = {
  id?: string;
  name: string;
  description: string;
  category_id: number;
  brand_id: number;
  base_price: string;
  image: string;
  status: 'active' | 'inactive';
  variants: Variant[];
  master_sku: string;
  stock_quantity: string;
  min_order_qty: string;
  order_multiple: string;   // ✅ NEW
};


const AddEditProductPage: React.FC<{ isEditing?: boolean }> = ({ isEditing = false }) => {
  const [hasGeneratedVariants, setHasGeneratedVariants] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [options, setOptions] = useState<Option[]>([]);
  const [categories, setCategories] = useState<{ id: number; name: string }[]>([]);
  const [newCategoryDialogOpen, setNewCategoryDialogOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [brands, setBrands] = useState<{ id: number; name: string }[]>([]);
const [newBrandDialogOpen, setNewBrandDialogOpen] = useState(false);
const [newBrandData, setNewBrandData] = useState({ name: '', logo: '', description: '' });

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const theme = useTheme();
  const { isDarkMode } = useThemeContext();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [form, setForm] = useState<ProductForm>({
    id: "",
    name: "",
    description: "",
    category_id: 0,
    brand_id: 0,
    base_price: "",
    image: "",
    status: "active",
    master_sku: "",
    stock_quantity: "0",
    min_order_qty: "1",
    order_multiple: "1", 
    variants: [],
  });

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const newCategory = await createCategory({ name: newCategoryName.trim() });
      setCategories([...categories, newCategory]);
      setForm({ ...form, category_id: newCategory.id });
      setNewCategoryDialogOpen(false);
      setNewCategoryName("");
    } catch (error) {
      console.error("Error creating category:", error);
    }
  };

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const fetchedCategories = await fetchProductCategories();
        setCategories(fetchedCategories);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    loadCategories();
  }, []);
  useEffect(() => {
    const loadBrands = async () => {
      try {
        const fetchedBrands = await fetchBrands();
        setBrands(fetchedBrands);
      } catch (error) {
        console.error("Error loading brands:", error);
      }
    };
    loadBrands();
  }, []);

 useEffect(() => {
  if (isEditing && id && categories.length > 0 && brands.length > 0) {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        if (!id || isNaN(Number(id))) {
          throw new Error("Invalid product ID");
        }

        const product = await fetchProductById(id);
        
        // Process variants
       const variants = product.variants?.map((variant: any) => ({
  id: variant.id || null,
  variant_name: variant.variant_name || "",
  sku: variant.sku || "",
  price: variant.price?.toString() || form.base_price || "0",
  stock_quantity: variant.stock_quantity?.toString() || "0",
  image: variant.image || "",
  selected_options: variant.selected_options || {},
 min_order_qty: variant.min_order_qty?.toString() || product.min_order_qty?.toString() || "1",
 order_multiple: variant.order_multiple?.toString() || product.order_multiple?.toString() || "1",
  isVisible: variant.isVisible !== false
})) || [];


        // Find matching category or fallback
        const productCategoryId = Number(product.category_id);
        const categoryInCategories = categories.find(c => c.id === productCategoryId);
        const uncategorizedCategory = categories.find(c => c.name === 'Uncategorized');
        const categoryId = categoryInCategories ? productCategoryId : (uncategorizedCategory?.id || 0);

        // Find matching brand or fallback
        const productBrandId = Number(product.brand_id);
        const brandInBrands = brands.find(b => b.id === productBrandId);
        const brandId = brandInBrands ? productBrandId : 0;

        // Process options with proper typing
        const options = product.options?.map((option: any) => ({
  id: option.id,
  name: option.name,
  values: option.values || [],
  isRequired: option.is_required ?? false   // 👈 map snake → camel
})) || [];


       setForm({
  id: String(product.product_id),
  name: product.name,
  description: product.description,
  category_id: categoryId,
  brand_id: brandId,
  status: product.status || "active",
  variants,
  master_sku: product.master_sku || product.name.toLowerCase().replace(/\s+/g, "-"),
  base_price: product.base_price?.toString() || "0",
  stock_quantity: product.stock_quantity?.toString() || "0",
  image: product.image || "",
  min_order_qty: product.min_order_qty?.toString() || "1", // 👈 Added
  order_multiple: product.order_multiple?.toString() || "1",

  
});


        setOptions(options);
        
        // Generate variants if options exist but no variants
        if (options.length > 0 && variants.length === 0) {
          const generatedVariants = generateVariants(options);
          setForm(prev => ({
            ...prev,
            variants: generatedVariants.map(v => ({
              ...v,
              price: prev.base_price || "0"
            }))
          }));
          setHasGeneratedVariants(true);
        }

      } catch (error) {
        console.error("Failed to fetch product:", error);
        setError("Failed to fetch product data");
        enqueueSnackbar("Failed to load product data", { variant: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  } else if (!isEditing) {
    // Reset form for new product
    setForm({
  id: "",
  name: "",
  description: "",
  category_id: categories[0]?.id || 0,
  brand_id: brands[0]?.id || 0,
  base_price: "0",
  image: "",
  status: "active",
  variants: [],
  master_sku: "",
  stock_quantity: "0",
  min_order_qty: "1",   // 👈 added default MOQ
  order_multiple: "1",
});
setOptions([]);

  }
}, [isEditing, id, categories, brands, enqueueSnackbar]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

const generateVariants = (options: Option[], existingVariants: Variant[] = []) => {
  if (!options.length) return [];
  
  // First ensure all options have values
  const validOptions = options.filter(opt => opt.values.length > 0);
  if (validOptions.length === 0) return [];

  // Create a map of existing variants by their selected_options
  const existingVariantMap = new Map(
    existingVariants.map(v => [
      JSON.stringify(v.selected_options || {}),
      v
    ])
  );

  const combinations: Record<string, string>[] = [];
  const keys = validOptions.map((opt) => opt.name);

  const combine = (curr: Record<string, string>, index: number) => {
    if (index === keys.length) {
      combinations.push(curr);
      return;
    }

    const option = validOptions[index];
    for (const val of option.values) {
      combine({ ...curr, [keys[index]]: val }, index + 1);
    }
  };

  combine({}, 0);

  return combinations.map((combo) => {
    const comboKey = JSON.stringify(combo);
    const existingVariant = existingVariantMap.get(comboKey);
    
    // If variant exists, keep it with all its properties
    if (existingVariant) {
      return existingVariant;
    }

    // Otherwise create new variant
    const name = Object.values(combo).join(" / ");
    const sku = `${form.master_sku}-${Object.values(combo)
      .map((v) => v.toLowerCase().replace(/\s+/g, "-"))
      .join("-")}`;

    return {
      variant_name: name,
      sku,
      price: form.base_price || "0",
      stock_quantity: "0",
      image: "",
      selected_options: combo,
    };
  });
};
  
const deduplicateOptions = (opts: Option[]): Option[] => {
  const seen = new Map<string, number>();
  return opts.filter((option, index) => {
    const key = `${option.name.toLowerCase()}`;
    if (seen.has(key)) {
      return false;
    }
    seen.set(key, index);
    return true;
  });
};

const handleSaveProduct = async () => {
  setLoading(true);
  setError(null);
  try {
    const productData: ProductUpdateData = {
  ...form,
  category_id: form.category_id,
  base_price: form.base_price,
  stock_quantity: form.stock_quantity,
  min_order_qty: form.min_order_qty,         // ✅ product MOQ
  order_multiple: form.order_multiple,       // ✅ product pack size
  variants: form.variants.map((variant) => ({
    ...variant,
    image: variant.image || "",
    price: variant.price,
    stock_quantity: variant.stock_quantity,
    selected_options: variant.selected_options || {},
    min_order_qty: (variant as any).min_order_qty?.toString() || "1",
    order_multiple: (variant as any).order_multiple?.toString() || form.order_multiple || "1", // ✅ variant pack fallback
  })),
      options: options.map((option) => ({
        ...option,
        isRequired: option.isRequired || false,
      })),
    };

    if (typeof productData.category_id !== "number") {
      throw new Error("Category is required");
    }

    if (isEditing && id) {
      await updateProduct(id, productData);
    } else {
      const result = await createProduct(productData);
      setForm((prev) => ({ ...prev, id: result.id }));
    }
    enqueueSnackbar("Product saved successfully!", { variant: "success" });
  } catch (error: any) {
    const rawMessage = error?.response?.data?.error || error.message || "Failed to save product";
    let friendlyMessage = rawMessage;
    if (rawMessage.includes("duplicate key value") && rawMessage.includes("products_master_sku_unique")) {
      friendlyMessage = "A product with this Master SKU already exists. Please choose a unique one.";
    }
    enqueueSnackbar(friendlyMessage, { variant: "error" });
    console.error("Save product failed:", error);
  } finally {
    setLoading(false);
  }
};


  const handleSaveAndClose = async () => {
    setLoading(true);
    try {
      await handleSaveProduct();
      navigate("/dashboard/products");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 1, md: 3 },
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: { xs: '100%', md: 'auto' } }}>
  <Button variant="outlined" onClick={() => navigate("/dashboard/products")} fullWidth={isMobile}>
    Cancel
  </Button>
  <Button 
    variant="contained" 
    onClick={handleSaveProduct} 
    disabled={loading} 
    fullWidth={isMobile}
    sx={{ mr: 1 }}
  >
    {loading ? 'Saving...' : 'Save'}
  </Button>
  <Button 
    variant="contained" 
    color="success"
    onClick={handleSaveAndClose} 
    disabled={loading} 
    fullWidth={isMobile}
  >
    {loading ? 'Saving...' : 'Save & Close'}
  </Button>
</Stack>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Tabs 
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          mb: 3,
          '& .MuiTabs-indicator': {
            height: 3,
          }
        }}
      >
        <Tab label="General" />
        <Tab label="Variants" />
        <Tab label="Media" />
      </Tabs>

      {activeTab === 0 && (
        <Card sx={{ mb: 3 }}>
          <CardHeader title="General Information" />
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={{xs: 12, md: 6}}>
                <TextField
                  label="Product Name"
                  value={form.name}
                  onChange={(e) => setForm({
                    ...form,
                    name: e.target.value,
                    master_sku: e.target.value.toLowerCase().replace(/\s+/g, "-")
                  })}
                  fullWidth
                  required
                />
              </Grid>
              
              <Grid size={{xs: 12, md: 6}}>
              <TextField
  select
  label="Category"
  value={categories.length > 0 ? form.category_id : ''}
  onChange={(e) => {
    const newCategoryId = Number(e.target.value);
    if (isNaN(newCategoryId)) return;
    setForm({ ...form, category_id: newCategoryId });
  }}
  required
  fullWidth
  disabled={categories.length === 0}
>
  {categories.length === 0 && (
    <MenuItem disabled value="">
      Loading categories...
    </MenuItem>
  )}
  {categories.map((category) => (
    <MenuItem key={category.id} value={category.id}>
      {category.name}
    </MenuItem>
  ))}
  <MenuItem onClick={() => setNewCategoryDialogOpen(true)}>
    + Create New Category
  </MenuItem>
</TextField>
              </Grid>

              <Grid size={{xs: 12, md: 6}}>
              <TextField
  select
  label="Brand"
  value={form.brand_id}
  onChange={(e) => setForm({ ...form, brand_id: parseInt(e.target.value) })}
  fullWidth
  required
>
  {brands.map((brand) => (
    <MenuItem key={brand.id} value={brand.id}>
      {brand.name}
    </MenuItem>
  ))}
  <MenuItem onClick={() => setNewBrandDialogOpen(true)}>
    + Create New Brand
  </MenuItem>
</TextField>


              </Grid>

              <Grid size={{xs: 12, md: 6}}>
                <TextField
                  label="Master SKU"
                  value={form.master_sku}
                  onChange={(e) => setForm({ ...form, master_sku: e.target.value })}
                  fullWidth
                  disabled={isEditing}
                />
              </Grid>

              <Grid size={{xs: 12, md: 6}}>
  <TextField
    label="Base Price"
    type="number"
    value={form.base_price}
    onChange={(e) => setForm({ ...form, base_price: e.target.value })}
    fullWidth
    inputProps={{ step: "0.01" }}
  />
</Grid>
<Grid size={{xs: 12, md: 6}}>
  <TextField
    label="Minimum Order Quantity"
    type="number"
    value={form.min_order_qty}
    onChange={(e) => setForm({ ...form, min_order_qty: e.target.value })}
    fullWidth
  />
</Grid>

<Grid size={{xs: 12, md: 6}}>
  <TextField
    label="Order Multiple"
    type="number"
    value={form.order_multiple}
    onChange={(e) => setForm({ ...form, order_multiple: e.target.value })}
    fullWidth
    helperText="Number of units per pack / must order in multiples of this"
  />
</Grid>


{form.variants.length === 0 && (
  <Grid size={{xs: 12, md: 6}}>
    <TextField
      label="Stock Quantity"
      type="number"
      value={form.stock_quantity}
      onChange={(e) => setForm({ ...form, stock_quantity: e.target.value })}
      fullWidth
    />
  </Grid>
)}


<Grid size={{xs: 12}}>
  <Box sx={{ 
    width: '100%',
    maxWidth: '100%',
    overflow: 'hidden'
  }}>
    <RichTextEditor
      value={form.description}
      onChange={(value) => setForm({ ...form, description: value })}
      theme={isDarkMode ? 'dark' : 'light'}
    />
  </Box>
</Grid>

              <Grid size={{xs: 12}}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.status === 'active'}
                      onChange={(e) => setForm({
                        ...form,
                        status: e.target.checked ? 'active' : 'inactive'
                      })}
                      color="primary"
                    />
                  }
                  label={`Product Status: ${form.status === 'active' ? 'Active' : 'Inactive'}`}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {activeTab === 1 && (
        <Stack spacing={2}>
          <Card>
            <CardHeader title="Variant Options" />
            <CardContent>
              <Box sx={{ width: "100%", maxWidth: '100%'}}>
<OptionsManager
  options={options}
  setOptions={setOptions}
  onOptionsChange={(newOptions, deletedOptionName) => {
    const dedupedOptions = deduplicateOptions(newOptions);
    setOptions(dedupedOptions);
    
    setForm(prev => {
      // If an option was deleted, filter out variants that used it
      const filteredVariants = deletedOptionName
        ? prev.variants.filter(v => !v.selected_options?.[deletedOptionName])
        : prev.variants;

      // Generate new variants while preserving existing ones
      const newVariants = generateVariants(dedupedOptions, filteredVariants);
      
      return {
        ...prev,
        variants: newVariants
      };
    });
  }}
/>

              </Box>
            </CardContent>
          </Card>

          <Card>
            {/* <CardHeader title="Variants" /> */}
            <CardContent>
              <VariantManager
                variants={form.variants}
                productId={parseInt(form.id || "0", 10)}
                optionNames={options.map(option => option.name)}
                onUpdateVariant={(index, field, value) => {
                  const updatedVariants = [...form.variants];
                  let convertedValue: string | number | boolean = value;
                  if (field === "stock_quantity") {
                    convertedValue = parseInt(value as string, 10) || 0;
                  } else if (field === "isVisible") {
                    convertedValue = Boolean(value);
                  }
                  updatedVariants[index] = { ...updatedVariants[index], [field]: convertedValue };
                  if (field === "stock_quantity" && updatedVariants[index].id !== undefined && updatedVariants[index].id !== null) {
                    const variantId = updatedVariants[index].id ?? null;
                    const stockValue = parseInt(value as string, 10);
                    if (!isNaN(stockValue)) {
                      updateVariantStock(parseInt(form.id || "0", 10), variantId, stockValue)
                        .then(() => console.log("Stock updated successfully."))
                        .catch((error) => console.error("Error updating stock:", error));
                    }
                  }
                  setForm({ ...form, variants: updatedVariants });
                }}
                onRemoveVariant={(index) => {
                  setForm({ ...form, variants: form.variants.filter((_, i) => i !== index) });
                }}
              />
            </CardContent>
          </Card>
<Card sx={{ mt: 3 }}>
  <CardContent>
  {form.id && Number(form.id) > 0 ? (
  <TieredPricingManager productId={parseInt(form.id)} />
) : (
  <Alert severity="info" sx={{ my: 2 }}>
    Please save the product first to configure tiered pricing.
  </Alert>
)}

  </CardContent>
</Card>

        </Stack>
      )}

      {activeTab === 2 && (
        <Card>
          <CardHeader title="Media" />
          <CardContent>
            <ImageUploader
              imageUrl={form.image}
              onImageChange={(url) => setForm({ ...form, image: url })}
            />
          </CardContent>
        </Card>
      )}

      <Dialog open={newCategoryDialogOpen} onClose={() => setNewCategoryDialogOpen(false)}>
        <DialogTitle>Create New Category</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Category Name"
            fullWidth
            value={newCategoryName}
            onChange={(e) => setNewCategoryName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCategoryDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCategory} disabled={!newCategoryName}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={newBrandDialogOpen} onClose={() => setNewBrandDialogOpen(false)}>
  <DialogTitle>Create New Brand</DialogTitle>
  <DialogContent>
    <Stack spacing={2}>
      <TextField
        label="Brand Name"
        fullWidth
        value={newBrandData.name}
        onChange={(e) => setNewBrandData({ ...newBrandData, name: e.target.value })}
      />
      <ImageUploader
        imageUrl={newBrandData.logo}
        onImageChange={(url) => setNewBrandData({ ...newBrandData, logo: url })}
      />
      <TextField
        label="Description"
        fullWidth
        multiline
        minRows={3}
        value={newBrandData.description}
        onChange={(e) => setNewBrandData({ ...newBrandData, description: e.target.value })}
      />
    </Stack>
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setNewBrandDialogOpen(false)}>Cancel</Button>
    <Button
      variant="contained"
      disabled={!newBrandData.name}
      onClick={async () => {
        try {
          const created = await createBrand(newBrandData);
          setBrands([...brands, created]);
          setForm({ ...form, brand_id: created.id });
          setNewBrandDialogOpen(false);
          setNewBrandData({ name: '', logo: '', description: '' });
        } catch (error) {
          enqueueSnackbar("Failed to create brand", { variant: "error" });
        }
      }}
    >
      Create
    </Button>
  </DialogActions>
</Dialog>


    </Box>
  );
};

export default AddEditProductPage;